import { useState, useEffect } from 'react';
import { Upload } from 'lucide-react';
import { authService } from '../../services/api';
import { useNotification } from '../Notifications/NotificationContext';
import PushTip from '../UI/PushTip/PushTip';
import ToggleCard from '../UI/Toggle/ToggleCard';
import BotSelector from '../BotSelector/BotSelector';

export default function ImportConfigModal({ open, bots, initialBot, onClose, onImported }) {
    const { showError, showSuccess } = useNotification();
    const [targetBot, setTargetBot] = useState(initialBot);
    const [keyValue, setKeyValue] = useState('');
    const [copyMedia, setCopyMedia] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        setTargetBot(initialBot);
        setKeyValue('');
        setCopyMedia(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    if (!open) return null;

    const handleImport = async () => {
        if (!targetBot) {
            showError('Selecione o bot de destino');
            return;
        }
        if (!keyValue.trim()) {
            showError('Cole uma Config Key para importar');
            return;
        }
        if (targetBot.config_key && keyValue.trim() === targetBot.config_key.trim()) {
            showError('Não é possível importar a Config Key do mesmo bot.');
            return;
        }

        const warn = copyMedia
            ? '⚠️ Ao importar, as mídias atuais do bot destino serão removidas e substituídas pelas do bot de origem (front, downsell, upsell e order bumps). Limite: 5 importações com mídia por dia. Continuar?'
            : '⚠️ Ao importar uma Config Key, todas as mídias do bot destino serão removidas. Continuar?';
        if (!window.confirm(warn)) return;

        try {
            setLoading(true);
            const res = await authService.bots.import_config(targetBot.id, keyValue.trim(), copyMedia);
            if (res.data?.success) {
                showSuccess(res.data.message || 'Config importada com sucesso');
                setKeyValue('');
                if (onImported) onImported(targetBot.id);
                onClose();
            } else {
                showError(res.data?.error || 'Erro ao importar configuração');
            }
        } catch (err) {
            showError(err?.response?.data?.error || err?.message || 'Erro ao importar configuração');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="manage-bots-modal-overlay" onClick={onClose}>
            <div className="manage-bots-modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
                <div className="manage-bots-modal-header">
                    <h3>
                        <Upload size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '6px' }} />
                        Importar Config
                    </h3>
                    <button type="button" className="manage-bots-close" onClick={onClose}>&times;</button>
                </div>

                <p className="text-sm text-white/40 mb-4">
                    Cole a Config Key de outro bot e escolha quem vai recebê-la.
                </p>

                <input
                    className="input3 w-full"
                    placeholder="Cole a Config Key que deseja importar"
                    value={keyValue}
                    onChange={(e) => setKeyValue(e.target.value)}
                    autoFocus
                />

                <label className="text-xs font-bold text-white/40 uppercase tracking-wider mb-2 mt-4 block">
                    Bot de destino
                </label>
                <BotSelector
                    bots={bots}
                    selectedBot={targetBot}
                    onSelect={setTargetBot}
                    allBotsOption={false}
                />

                <div style={{ marginTop: 12 }}>
                    <ToggleCard
                        title="Copiar mídias junto"
                        icon="fas fa-photo-film"
                        checked={copyMedia}
                        onChange={(v) => setCopyMedia(!!v)}
                    />
                    {copyMedia && (
                        <p className="text-xs text-white/40 mt-2">
                            Copia as mídias do fluxo padrão (front, downsell, upsell) e dos order bumps (padrão e personalizados).
                            Requer o bot de origem ativo e o grupo de logs configurado no bot destino. Limite: 5 por dia.
                        </p>
                    )}
                </div>

                <PushTip>
                    A importação de uma config_key resultará na exclusão definitiva de todas as mídias e áudios configurados no bot destino, uma vez que esses arquivos não fazem parte da chave de configuração.
                </PushTip>

                <div className="manage-bots-footer mt-2" style={{ marginTop: 12 }}>
                    <div />
                    <div className="manage-bots-footer-actions">
                        <button type="button" className="btn-secondary" onClick={onClose} disabled={loading}>Cancelar</button>
                        <button type="button" className="btn-primary" onClick={handleImport} disabled={loading || !targetBot || !keyValue.trim()}>
                            {loading ? 'Importando...' : 'Importar'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
